import React, { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import FloatingShapes from "@/components/FloatingShapes";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { BookOpen, Sparkles, Layout, GraduationCap, Upload, Loader2, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";

const GUEST_ID_KEY = "loom_guest_id";
const GUEST_DECKS_KEY = "loom_guest_deck_ids";
const GUEST_DECK_LIMIT = 5;

function getGuestId() {
  let id = localStorage.getItem(GUEST_ID_KEY);
  if (!id) {
    id = (crypto.randomUUID ? crypto.randomUUID().replace(/-/g, "") : Math.random().toString(36).slice(2, 26) + Math.random().toString(36).slice(2, 10)).slice(0, 32);
    localStorage.setItem(GUEST_ID_KEY, id);
  }
  return id;
}

function getGuestDeckIds() {
  try { return JSON.parse(localStorage.getItem(GUEST_DECKS_KEY) || "[]"); } catch { return []; }
}

export default function Landing() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);
  const [showLimitModal, setShowLimitModal] = useState(false);

  const [dragging, setDragging] = useState(false);

  const handleFile = async (file) => {
    if (!file || uploading) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      setUploadError("Only PDF files are supported.");
      return;
    }
    const deckIds = getGuestDeckIds();
    if (deckIds.length >= GUEST_DECK_LIMIT) {
      setShowLimitModal(true);
      return;
    }
    setUploading(true);
    setProgress(0);
    setUploadError(null);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await axios.post(`${API}/documents/upload`, fd, {
        headers: { "X-Guest-Id": getGuestId() },
        withCredentials: true,
        onUploadProgress: (ev) => setProgress(Math.round((ev.loaded * 100) / (ev.total || 1))),
      });
      const ids = getGuestDeckIds();
      ids.push(r.data.doc_id);
      localStorage.setItem(GUEST_DECKS_KEY, JSON.stringify(ids));
      nav(`/deck/${r.data.doc_id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Upload failed — please try again.";
      setUploadError(detail);
      setUploading(false);
      setProgress(0);
    }
  };

  const onInputChange = (e) => handleFile(e.target.files?.[0]);
  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files?.[0]);
  };

  return (
    <div className="relative min-h-screen grain" data-testid="landing-page">
      <FloatingShapes />
      <Nav />

      {/* Guest deck-limit modal */}
      {showLimitModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={() => setShowLimitModal(false)}
          data-testid="guest-limit-modal-overlay"
        >
          <div
            className="sticky-card relative w-full max-w-md p-8 text-center"
            onClick={(e) => e.stopPropagation()}
            data-testid="guest-limit-modal"
          >
            <button
              onClick={() => setShowLimitModal(false)}
              className="absolute top-4 right-4 text-stone-400 hover:text-stone-700 text-xl leading-none"
              aria-label="Close"
              data-testid="guest-limit-modal-close"
            >✕</button>
            <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-5">
              <span className="text-2xl">📚</span>
            </div>
            <h2 className="font-display text-3xl leading-tight">You've used your 5 free decks</h2>
            <p className="text-stone-600 mt-3 leading-relaxed">
              Free visitors can create up to 5 study decks. Upgrade to Pro for unlimited uploads, full deck access, exports, and no ads.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 mt-7">
              <Link
                to="/pricing"
                className="btn-primary flex-1 text-center"
                data-testid="guest-limit-upgrade-btn"
                onClick={() => setShowLimitModal(false)}
              >
                See plans →
              </Link>
              <Link
                to="/login"
                className="btn-ghost flex-1 text-center"
                data-testid="guest-limit-login-btn"
                onClick={() => setShowLimitModal(false)}
              >
                Sign in
              </Link>
            </div>
            <p className="text-stone-400 text-xs mt-5">
              Already created decks? Sign in to access them.
            </p>
          </div>
        </div>
      )}
      <main className="relative z-10 max-w-6xl mx-auto px-6 pt-32 pb-24">
        <motion.div
          initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9 }}
          className="text-center"
        >
          <span className="tag-pill" data-testid="hero-tag">A study atelier · not a reader</span>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-8 leading-[1.04] tracking-tight">
            Turn long PDFs into
            <br/><span className="marker">horizontal study decks.</span>
          </h1>
          <p className="mt-8 max-w-2xl mx-auto text-lg sm:text-xl text-stone-600 leading-relaxed">
            Master any document in hours, not days — designed for high pressure, less time, and maximum retention.
          </p>

          {/* Upload zone — no login required */}
          <div className="mt-10 max-w-xl mx-auto">
            <div
              role="button"
              tabIndex={0}
              onClick={() => !uploading && fileRef.current?.click()}
              onKeyDown={(e) => e.key === "Enter" && !uploading && fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              data-testid="hero-upload-zone"
              className={`relative cursor-pointer rounded-2xl border-2 border-dashed px-8 py-10 transition-all duration-200
                ${dragging ? "border-indigo-500 bg-indigo-50" : "border-stone-300 hover:border-indigo-400 hover:bg-stone-50"}
                ${uploading ? "cursor-not-allowed opacity-75" : ""}`}
            >
              <input ref={fileRef} type="file" accept="application/pdf" className="hidden" onChange={onInputChange} data-testid="hero-upload-input" />
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-indigo-500 animate-spin" />
                  <p className="font-display text-xl text-stone-700">Building your deck…</p>
                  <div className="w-full max-w-xs bg-stone-200 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-2 bg-indigo-500 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                      data-testid="upload-progress-bar"
                    />
                  </div>
                  <p className="text-sm text-stone-500">{progress}% uploaded</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-indigo-100 flex items-center justify-center">
                    <Upload className="w-6 h-6 text-indigo-600" />
                  </div>
                  <p className="font-display text-xl text-stone-800">Drop your PDF here</p>
                  <p className="text-stone-500 text-sm">or click to browse — no sign-up required</p>
                  <button className="btn-primary inline-flex items-center gap-2 mt-1" data-testid="hero-cta-start">
                    <Upload className="w-4 h-4" /> Upload Your PDF Free
                  </button>
                </div>
              )}
            </div>
            {uploadError && (
              <p className="mt-3 text-sm text-red-600 text-center" data-testid="upload-error">{uploadError}</p>
            )}
            <p className="mt-4 text-stone-500 text-sm text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-indigo-600 hover:underline" data-testid="hero-cta-login">Sign in to your decks</Link>
            </p>
          </div>

          <div className="mt-6 font-hand text-stone-500 text-xl">no dark mode, no spreadsheets, no corporate energy</div>
        </motion.div>

        <div className="mt-28 grid sm:grid-cols-3 gap-8">
          {[
            { icon: BookOpen,   t: "Upload anything", d: "PDFs up to 1000 pages. Loom slices long docs into bite-sized decks you can finish in a sitting." },
            { icon: Layout,     t: "See the whole shape", d: "Scroll sideways, not down. Each section is a floating block with the must-remember pieces highlighted." },
            { icon: GraduationCap, t: "Recall, don't reread", d: "Flip-card flashcards, MCQs, and recall prompts generated from your own document." },
          ].map((f, i) => (
            <motion.div
              key={i} initial={{opacity:0, y:30}} whileInView={{opacity:1, y:0}} viewport={{once:true}}
              transition={{delay: i*0.15, duration: 0.7}}
              className="sticky-card p-8" data-testid={`feature-card-${i}`}
            >
              <f.icon className="w-7 h-7 text-indigo-500"/>
              <div className="font-display text-2xl mt-5">{f.t}</div>
              <p className="text-stone-600 mt-3 leading-relaxed">{f.d}</p>
            </motion.div>
          ))}
        </div>

        <section className="mt-28 sticky-card p-10 sm:p-14 relative overflow-hidden">
          <div className="absolute -top-10 -right-10 w-48 h-48 rounded-[40%] bg-yellow-200/60 drift-slow"/>
          <div className="absolute -bottom-12 -left-10 w-36 h-36 rounded-full bg-emerald-200/60 drift"/>
          <div className="relative z-10 max-w-2xl">
            <span className="tag-pill">the loop</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-tight">Upload · Wander · Quiz · Repeat.</h2>
            <p className="text-stone-600 mt-5 text-lg">
              Drop a PDF. Loom sketches it into sections within seconds. Walk the horizontal hallway, flip the
              flashcards, answer the MCQs, and let the recall prompts cement what's slippery.
            </p>
            <button onClick={() => fileRef.current?.click()} className="btn-primary mt-8 inline-block" data-testid="footer-cta">Try it on your next syllabus</button>
          </div>
        </section>

        {/* Keyword-rich, crawlable content section for SEO ("study from PDF", "PDF to flashcards", "AI quiz") */}
        <section className="mt-28 max-w-3xl mx-auto" data-testid="how-it-works" aria-labelledby="how-heading">
          <span className="tag-pill">how it works</span>
          <h2 id="how-heading" className="font-display text-4xl sm:text-5xl mt-5 leading-tight">
            How to study from a PDF with Loom
          </h2>
          <p className="text-stone-600 mt-5 text-lg leading-relaxed">
            Loom is a PDF study tool built for students, exam candidates and lifelong learners who
            are tired of scrolling endless documents. Instead of one long vertical wall of text,
            Loom turns any PDF — textbooks, lecture notes, research papers, interview prep or a
            whitepaper — into a horizontal deck of study sections you can actually walk through and
            remember. Here is the three-step study workflow.
          </p>

          <div className="mt-10 space-y-8">
            <div>
              <h3 className="font-display text-2xl flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 inline-flex items-center justify-center text-base">1</span>
                Upload your PDF
              </h3>
              <p className="text-stone-600 mt-3 leading-relaxed">
                Drag in any PDF up to 1000 pages. Loom reads the document and uses AI to break it
                into 6–14 clear, self-contained study sections — one idea per block — so a 300-page
                textbook becomes a deck you can finish in a single focused sitting.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-emerald-100 text-emerald-600 inline-flex items-center justify-center text-base">2</span>
                Walk through the sections
              </h3>
              <p className="text-stone-600 mt-3 leading-relaxed">
                Scroll sideways through vivid, distraction-free cards. Each section shows a summary,
                the key points worth memorising, and a deeper-recall paragraph — with page references
                back to the original PDF so you can always check the source while you revise.
              </p>
            </div>
            <div>
              <h3 className="font-display text-2xl flex items-center gap-3">
                <span className="w-8 h-8 rounded-full bg-amber-100 text-amber-600 inline-flex items-center justify-center text-base">3</span>
                Turn the PDF into quizzes &amp; flashcards
              </h3>
              <p className="text-stone-600 mt-3 leading-relaxed">
                With one click Loom generates flashcards, multiple-choice questions and active-recall
                prompts straight from your document — the fastest way to convert a PDF into an exam-prep
                quiz. Export any deck or quiz to PDF or PowerPoint to study offline or share with a study group.
              </p>
            </div>
          </div>

          <div className="mt-12">
            <h2 className="font-display text-3xl leading-tight">Frequently asked questions</h2>
            <div className="mt-6 space-y-6">
              <div>
                <h3 className="font-medium text-stone-800">Can Loom turn a PDF into flashcards and a quiz?</h3>
                <p className="text-stone-600 mt-2 leading-relaxed">
                  Yes. Loom auto-generates flashcards, MCQs and recall questions from the exact content
                  of your PDF, so every question is grounded in your own study material — not generic trivia.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-stone-800">What kinds of PDFs work best?</h3>
                <p className="text-stone-600 mt-2 leading-relaxed">
                  Textbooks, lecture slides, class notes, research papers, competitive-exam material and
                  professional whitepapers all work well. Loom is designed for dense, text-heavy study documents.
                </p>
              </div>
              <div>
                <h3 className="font-medium text-stone-800">Is there a free plan?</h3>
                <p className="text-stone-600 mt-2 leading-relaxed">
                  Yes — you can start free and turn your first PDFs into study decks with no card required.
                  See the <Link to="/pricing" className="text-indigo-600 underline">pricing page</Link> for Pro and Lifetime options.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      <p className="relative z-10 text-center pt-10 text-stone-500 font-hand text-lg">
        woven slowly · for students, candidates, and curious people
      </p>
      <Footer />
    </div>
  );
}
